import { motion } from 'framer-motion';
import { useCallback } from 'react';

export function Hero() {
  const scrollToContact = useCallback(() => {
    const contactSection = document.getElementById('contact');
    contactSection?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const scrollToServices = useCallback(() => {
    const servicesSection = document.getElementById('services');
    servicesSection?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/d9eadd73ddd0229cff4dcb258194a6d6.jpg"
          alt="Expert en réparation de fenêtres à Bordeaux"
          title="Réparation fenêtres Bordeaux - Expert vitrier"
          className="w-full h-full object-cover object-top"
          loading="eager"
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-teal/90 via-teal/80 to-cyan-600/85"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Expert Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              {/* Main Image */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl w-full h-[500px]">
                <img
                  src="https://readdy.ai/api/search-image?query=friendly%20smiling%20window%20repair%20technician%20specialist%20wearing%20casual%20gray%20work%20shirt%20holding%20professional%20window%20repair%20toolkit%20and%20replacement%20parts%20standing%20in%20bright%20contemporary%20service%20workshop%20with%20large%20glass%20windows%20in%20background%20professional%20lighting%20showcasing%20approachable%20customer%20service%20expertise%20and%20modern%20residential%20window%20repair%20maintenance%20solutions&width=600&height=700&seq=bordeaux-window-repair-specialist-modern-v9&orientation=portrait"
                  alt="Expert FenetrePro Bordeaux - Technicien vitrier professionnel"
                  title="Artisan vitrier Bordeaux - Réparation fenêtres"
                  className="w-full h-full object-cover object-top"
                  loading="eager"
                />
              </div>

              {/* Floating Badge */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 1 }}
                className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-2xl p-6"
                role="complementary"
                aria-label="Badge d'expérience"
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-orange rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-award-line text-white text-3xl" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-900">15+</div>
                    <div className="text-sm text-gray-600 font-medium">Ans d'Expérience</div>
                  </div>
                </div>
              </motion.div>

              {/* Decorative Elements */}
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-orange/20 rounded-full blur-2xl" aria-hidden="true"></div>
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/20 rounded-full blur-3xl" aria-hidden="true"></div>
            </div>
          </motion.div>

          {/* Right Column - Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-block mb-6 px-5 py-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30"
            >
              <span className="text-white font-medium text-sm">
                ⭐ Expert en Réparation de Fenêtres à Bordeaux
              </span>
            </motion.div>

            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Réparation de Fenêtres
              <span className="block text-orange mt-2">à Bordeaux</span>
            </h1>

            <p className="text-xl text-white/95 mb-10 leading-relaxed">
              Intervention sous 24h pour tous vos problèmes de fenêtres, baies vitrées et volets roulants à Bordeaux. 
              Devis gratuit et garantie sur tous nos travaux.
            </p>

            {/* Trust Points */}
            <ul className="space-y-4 mb-10" role="list">
              {[
                'Intervention rapide sous 24h',
                'Devis gratuit et sans engagement',
                'Garantie 2 ans sur toutes réparations',
                'Artisans certifiés et expérimentés'
              ].map((point, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-6 h-6 bg-orange rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-white text-sm" aria-hidden="true"></i>
                  </div>
                  <span className="text-white font-medium">{point}</span>
                </motion.li>
              ))}
            </ul>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                onClick={scrollToContact}
                className="px-8 py-4 bg-orange hover:bg-orange-dark text-white font-bold rounded-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
                aria-label="Obtenir un devis gratuit"
              >
                <span>Devis Gratuit</span>
                <i className="ri-arrow-right-line text-xl" aria-hidden="true"></i>
              </motion.button>

              <motion.a
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 }}
                href="tel:+33554542545"
                className="px-8 py-4 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white font-bold rounded-xl border-2 border-white/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
                aria-label="Appeler FenetrePro Bordeaux"
              >
                <i className="ri-phone-line text-xl" aria-hidden="true"></i>
                <span>05 54 54 25 45</span>
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.button
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center gap-2 cursor-pointer bg-transparent border-0"
          onClick={scrollToServices}
          aria-label="Découvrir nos services"
        >
          <span className="text-white/80 text-sm font-medium">Découvrir</span>
          <div className="w-6 h-10 border-2 border-white/40 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
          </div>
        </motion.button>
      </motion.div>
    </section>
  );
}